import React, { useState, useRef, useEffect } from "react";
import {
  Search, X, Loader2, Truck, Wrench, GitBranch,
  Package, Users, ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGlobalSearchQuery, SearchCategory } from "../../redux/services/searchApi";
import { FLEETRIFYROUTEPATH } from "../../routes/routesPath";


function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const CATEGORIES: { label: string; value: SearchCategory }[] = [
  { label: "All",      value: "all"      },
  { label: "Users",    value: "users"    },
  { label: "Orders",   value: "orders"   },
  { label: "Fleet",    value: "fleet"    },
  { label: "Services", value: "services" },
  { label: "Branches", value: "branches" },
];

const STATUS_COLORS: Record<string, string> = {
  active:     "bg-green-100 text-green-700",
  completed:  "bg-blue-100 text-blue-700",
  pending:    "bg-amber-100 text-amber-700",
  cancelled:  "bg-red-100 text-red-700",
  processing: "bg-purple-100 text-purple-700",
};


const ResultSection = ({
  title, icon, children,
}: {
  title: string; icon: React.ReactNode; children: React.ReactNode;
}) => (
  <div className="mb-1">
    <div className="flex items-center gap-1.5 px-4 py-1.5">
      <span className="text-[#143887]">{icon}</span>
      <span className="text-[10px] font-semibold text-[#143887] uppercase tracking-wider">
        {title}
      </span>
    </div>
    {children}
  </div>
);

const ResultRow = ({
  primary, secondary, badge, onClick,
}: {
  primary: string; secondary?: string; badge?: string; onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#f0f4fb] transition-colors group"
  >
    <div className="min-w-0">
      <p className="text-sm font-medium text-[#00032A] truncate group-hover:text-[#143887] transition-colors">
        {primary}
      </p>
      {secondary && (
        <p className="text-xs text-gray-400 truncate mt-0.5">{secondary}</p>
      )}
    </div>
    {badge && (
      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${STATUS_COLORS[badge.toLowerCase()] ?? "bg-gray-100 text-gray-600"}`}>
        {badge}
      </span>
    )}
  </button>
);


const SearchBody = ({
  category,
  setCategory,
  shouldSearch,
  searching,
  debouncedQuery,
  totalCount,
  showUsers,
  showFleet,
  showServices,
  showBranches,
  showOrders,
  results,
  goTo,
  bodyClass = "",
}: {
  category: SearchCategory;
  setCategory: (c: SearchCategory) => void;
  shouldSearch: boolean;
  searching: boolean;
  debouncedQuery: string;
  totalCount: number;
  showUsers: boolean;
  showFleet: boolean;
  showServices: boolean;
  showBranches: boolean;
  showOrders: boolean;
  results: any;
  goTo: (path: string) => void;
  bodyClass?: string;
}) => (
  <>
    {/* Category pills */}
    <div className="flex items-center gap-1.5 px-3 pt-3 pb-2.5 flex-wrap border-b border-gray-100">
      {CATEGORIES.map((cat) => (
        <button
          key={cat.value}
          type="button"
          onClick={() => setCategory(cat.value)}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            category === cat.value
              ? "bg-[#143887] text-white shadow-sm"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {cat.label}
        </button>
      ))}
    </div>

    {/* Results body */}
    <div className={`overflow-y-auto ${bodyClass}`}>
      {!shouldSearch && (
        <p className="px-4 py-10 text-center text-xs text-gray-400">
          Type at least 2 characters to search
        </p>
      )}

      {searching && shouldSearch && (
        <div className="flex items-center justify-center gap-2 py-10">
          <Loader2 size={16} className="animate-spin text-[#143887]" />
          <span className="text-xs text-gray-500">Searching...</span>
        </div>
      )}

      {!searching && shouldSearch && totalCount === 0 && (
        <div className="px-4 py-10 text-center">
          <p className="text-xs text-gray-400">
            No results for{" "}
            <span className="font-semibold text-gray-600">"{debouncedQuery}"</span>
          </p>
          <p className="text-[11px] text-gray-300 mt-1">
            Try a different keyword or category
          </p>
        </div>
      )}

      {!searching && totalCount > 0 && (
        <>
          {showUsers && (
            <ResultSection title="Users" icon={<Users size={12} />}>
              {results.users.map((item: any) => {
                const displayName =
                  item.name ||
                  [item.firstName, item.lastName].filter(Boolean).join(" ") ||
                  "Unknown user";
                const roleName =
                  typeof item.role === "object" ? item.role?.name : item.role;
                return (
                  <ResultRow
                    key={item.id}
                    primary={displayName}
                    secondary={[item.email, roleName].filter(Boolean).join(" · ")}
                    onClick={() => goTo(FLEETRIFYROUTEPATH.USER)}
                  />
                );
              })}
            </ResultSection>
          )}

          {showFleet && (
            <ResultSection title="Fleet" icon={<Truck size={12} />}>
              {results.fleet.map((item: any) => (
                <ResultRow
                  key={item.id}
                  primary={item.license_plate || item.vin || "Unknown vehicle"}
                  secondary={[item.vehicle_type, item.model_id].filter(Boolean).join(" · ")}
                  onClick={() => goTo(FLEETRIFYROUTEPATH.FLEET_MANAGEMENT)}
                />
              ))}
            </ResultSection>
          )}

          {showServices && (
            <ResultSection title="Services" icon={<Wrench size={12} />}>
              {results.services.map((item: any) => (
                <ResultRow
                  key={item.id}
                  primary={item.description || "Unnamed service"}
                  secondary={item.vehicle_type}
                  onClick={() =>
                    goTo(
                      item.id
                        ? `/services/view-service/${item.id}`
                        : FLEETRIFYROUTEPATH.SERVICES
                    )
                  }
                />
              ))}
            </ResultSection>
          )}

          {showBranches && (
            <ResultSection title="Branches" icon={<GitBranch size={12} />}>
              {results.branches.map((item: any) => (
                <ResultRow
                  key={item.id}
                  primary={item.branch_name || "Unnamed branch"}
                  secondary={item.branch_address || item.branch_email}
                  onClick={() => goTo(FLEETRIFYROUTEPATH.BRANCH)}
                />
              ))}
            </ResultSection>
          )}

          {showOrders && (
            <ResultSection title="Orders" icon={<Package size={12} />}>
              {results.orders.map((item: any) => (
                <ResultRow
                  key={item.id}
                  primary={item.order_number || "Unknown order"}
                  secondary={item.order_type}
                  badge={item.status}
                  onClick={() =>
                    goTo(
                      item.id
                        ? `/orders/view-order/${item.id}`
                        : FLEETRIFYROUTEPATH.ORDERS
                    )
                  }
                />
              ))}
            </ResultSection>
          )}

          <div className="border-t border-gray-50 px-4 py-2">
            <p className="text-[10px] text-gray-300">
              {totalCount} result{totalCount !== 1 ? "s" : ""} for "{debouncedQuery}"
            </p>
          </div>
        </>
      )}
    </div>
  </>
);


export const GlobalSearch: React.FC = () => {
  const [query, setQuery]       = useState("");
  const [category, setCategory] = useState<SearchCategory>("all");
  const [isOpen, setIsOpen]     = useState(false);
  const containerRef            = useRef<HTMLDivElement>(null);
  const inputRef                = useRef<HTMLInputElement>(null);
  const navigate                = useNavigate();

  const debouncedQuery = useDebounce(query, 350);
  const shouldSearch   = debouncedQuery.trim().length >= 2;

  const { data, isLoading, isFetching } = useGlobalSearchQuery(
    { query: debouncedQuery.trim(), category },
    { skip: !shouldSearch }
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setIsOpen(false); inputRef.current?.blur(); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const goTo = (path: string) => { navigate(path); setIsOpen(false); };

  const searching     = isLoading || isFetching;
  const results       = data?.results;
  const usersCount    = results?.users?.length    ?? 0;
  const fleetCount    = results?.fleet?.length    ?? 0;
  const servicesCount = results?.services?.length ?? 0;
  const branchesCount = results?.branches?.length ?? 0;
  const ordersCount   = results?.orders?.length   ?? 0;
  const totalCount    = usersCount + fleetCount + servicesCount + branchesCount + ordersCount;

  const showUsers    = (category === "all" || category === "users")    && usersCount    > 0;
  const showFleet    = (category === "all" || category === "fleet")    && fleetCount    > 0;
  const showServices = (category === "all" || category === "services") && servicesCount > 0;
  const showBranches = (category === "all" || category === "branches") && branchesCount > 0;
  const showOrders   = (category === "all" || category === "orders")   && ordersCount   > 0;

  return (
    <div ref={containerRef} className="relative w-full md:w-[380px]">
      {/* Input */}
      <div className="relative flex items-center">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
          <Search size={16} />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search users, orders, fleet, services..."
          className="w-full pl-10 pr-9 py-2.5 rounded-full bg-[#F5F6FA] text-sm outline-none placeholder:text-[#828282] placeholder:font-light focus:ring-1 focus:ring-[#143887] transition-all"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full mt-2 left-0 w-full min-w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-[9999] overflow-hidden">
          <SearchBody
            category={category}
            setCategory={setCategory}
            shouldSearch={shouldSearch}
            searching={searching}
            debouncedQuery={debouncedQuery}
            totalCount={totalCount}
            showUsers={showUsers}
            showFleet={showFleet}
            showServices={showServices}
            showBranches={showBranches}
            showOrders={showOrders}
            results={results}
            goTo={goTo}
            bodyClass="max-h-[380px]"
          />
        </div>
      )}
    </div>
  );
};


export const MobileSearch: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [query, setQuery]       = useState("");
  const [category, setCategory] = useState<SearchCategory>("all");
  const inputRef                = useRef<HTMLInputElement>(null);
  const navigate                = useNavigate();

  const debouncedQuery = useDebounce(query, 350);
  const shouldSearch   = debouncedQuery.trim().length >= 2;

  const { data, isLoading, isFetching } = useGlobalSearchQuery(
    { query: debouncedQuery.trim(), category },
    { skip: !shouldSearch }
  );

  // Auto-focus
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Escape closes overlay
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const goTo = (path: string) => { navigate(path); onClose(); };

  const searching     = isLoading || isFetching;
  const results       = data?.results;
  const usersCount    = results?.users?.length    ?? 0;
  const fleetCount    = results?.fleet?.length    ?? 0;
  const servicesCount = results?.services?.length ?? 0;
  const branchesCount = results?.branches?.length ?? 0;
  const ordersCount   = results?.orders?.length   ?? 0;
  const totalCount    = usersCount + fleetCount + servicesCount + branchesCount + ordersCount;

  const showUsers    = (category === "all" || category === "users")    && usersCount    > 0;
  const showFleet    = (category === "all" || category === "fleet")    && fleetCount    > 0;
  const showServices = (category === "all" || category === "services") && servicesCount > 0;
  const showBranches = (category === "all" || category === "branches") && branchesCount > 0;
  const showOrders   = (category === "all" || category === "orders")   && ordersCount   > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Input row */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-gray-100">
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
          aria-label="Close search"
        >
          <ArrowLeft size={20} />
        </button>

        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
            <Search size={16} />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users, orders, fleet, services..."
            className="w-full pl-10 pr-9 py-2.5 rounded-full bg-[#F5F6FA] text-sm outline-none placeholder:text-[#828282] placeholder:font-light focus:ring-1 focus:ring-[#143887] transition-all"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); inputRef.current?.focus(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable results — fills remaining screen height */}
      <div className="flex-1 overflow-hidden">
        <SearchBody
          category={category}
          setCategory={setCategory}
          shouldSearch={shouldSearch}
          searching={searching}
          debouncedQuery={debouncedQuery}
          totalCount={totalCount}
          showUsers={showUsers}
          showFleet={showFleet}
          showServices={showServices}
          showBranches={showBranches}
          showOrders={showOrders}
          results={results}
          goTo={goTo}
          bodyClass="h-full"
        />
      </div>
    </div>
  );
};

export default GlobalSearch;
